import { TogglzFeature } from '@sfly/cart-shared-library';

export const CART_VARIANT_STORAGE_KEY = 'sfly_cart_slidein_variant';
export const CART_VARIANT_COOKIE_MAX_AGE = 30 * 24 * 60 * 60;
export const ALLOY_TIMEOUT_MS = 2000;

export type CartVariant = 'A' | 'B' | 'C';

const VALID_VARIANTS: ReadonlyArray<CartVariant> = ['A', 'B', 'C'];

type AlloyFn = (
  command: string,
  options: Record<string, unknown>,
) => Promise<unknown>;

export function readActivityNameFromTogglz(
  togglzFlags: TogglzFeature[],
): string | null {
  const flagInfo = togglzFlags.find(
    (flag) => flag.uid === 'cart.cartapp.shutterfly-us.enable_slidein_cart',
  );
  const activityName = flagInfo?.customProperties?.activityName?.value;
  return typeof activityName === 'string' ? activityName : null;
}

function getCookieName(): string {
  return process.env.ENV === 'prod'
    ? CART_VARIANT_STORAGE_KEY
    : `${CART_VARIANT_STORAGE_KEY}_test`;
}

function getCookieDomain(): string {
  const domains = {
    prod: '.shutterfly.com',
    stage: '.stage.shutterfly.com',
    dev: '.dev.shutterfly.com',
  };

  return (
    domains[process.env.ENV as keyof typeof domains] ?? '.dev.shutterfly.com'
  );
}

function isValidVariant(value: unknown): value is CartVariant {
  return (
    typeof value === 'string' &&
    (VALID_VARIANTS as ReadonlyArray<string>).includes(value)
  );
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Domain=${getCookieDomain()}; Path=/; SameSite=Lax; Secure;`;
}

function readSession(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage may throw in private browsing or when disabled
  }
}

function cacheVariant(variant: CartVariant): CartVariant {
  writeSession(CART_VARIANT_STORAGE_KEY, variant);
  writeCookie(getCookieName(), variant, CART_VARIANT_COOKIE_MAX_AGE);
  return variant;
}

function readCachedVariant(): CartVariant | null {
  const fromSession = readSession(CART_VARIANT_STORAGE_KEY);
  if (isValidVariant(fromSession)) return fromSession;
  const fromCookie = readCookie(getCookieName());
  return isValidVariant(fromCookie) ? fromCookie : null;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('alloy timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export function readResolvedVariant(): CartVariant {
  return readCachedVariant() ?? 'A';
}

export async function resolveVariant(
  activityName: string | null,
): Promise<CartVariant> {
  const cached = readCachedVariant();
  if (cached) return cached;

  const alloy = (window as unknown as { alloy?: AlloyFn }).alloy;
  if (typeof alloy !== 'function') return 'A';

  try {
    const result = (await withTimeout(
      alloy('sendEvent', {
        renderDecisions: true,
        decisionScopes: ['__view__'],
        xdm: {
          eventType: 'decisioning.propositionInteract',
          web: {
            webPageDetails: {
              URL: window.location.href + '?cartSlideIn=true',
            },
          },
        },
      }),
      ALLOY_TIMEOUT_MS,
    )) as {
      propositions?: Array<{
        scope?: string;
        id?: unknown;
        scopeDetails?: unknown;
        items?: Array<{
          data?: { content?: { experience?: unknown } };
          meta?: { 'activity.name'?: string };
        }>;
      }>;
    };

    const proposition = result?.propositions?.find(
      (p) => p?.items?.[0]?.meta?.['activity.name'] === activityName,
    );
    const rawVariant = proposition?.items?.[0]?.data?.content?.experience;
    const variant: CartVariant = isValidVariant(rawVariant) ? rawVariant : 'A';

    return cacheVariant(variant);
  } catch {
    return 'A';
  }
}
